import React from 'react'
import { Link } from 'react-router-dom'
import { Button, Checkbox, Form, Input, Typography } from 'antd'
import {
  LockOutlined,
  LoginOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons'
import { authApi } from 'api/auth'
import { appActions } from 'store/app'
import { ELoginTypes, ERoles, ITokensResponse } from 'models/auth'
import { routes } from 'shared/constants/routes'
import { SUPPORT_URL } from 'shared/constants/urls'
import { FRESH_LOGIN_STORAGE_KEY } from 'shared/constants/storage'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { showServerError } from 'shared/utils/showServerError'
import { SignInPhone } from './SignInPhone'
import styles from './SignIn.module.scss'

interface ISignInForm {
  email: string
  password: string
}

// вход по телефону основной, почта — запасной способ (.AUTH_RULES.md §2)
type TSignInMethod = 'phone' | 'email'

interface ISignInResult extends ITokensResponse {
  role: ERoles
  isSeller: boolean
}

interface ISignInProps {
  setActiveScreenPassword: () => void
  setActiveScreenSeller: () => void
  setIsEmptyPassword: (isEmpty: boolean) => void
  setToken: (token: string) => void
}

export const SignIn: React.FC<ISignInProps> = ({
  setActiveScreenPassword,
  setActiveScreenSeller,
  setIsEmptyPassword,
  setToken
}) => {
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = React.useState(false)
  const [method, setMethod] = React.useState<TSignInMethod>('phone')

  const setPhoneMethod = React.useCallback(() => setMethod('phone'), [])
  const setEmailMethod = React.useCallback(() => setMethod('email'), [])

  const applyLogin = React.useCallback(
    (data: ISignInResult) => {
      if (data.role === ERoles.ADMIN) {
        return dispatch(appActions.setToken(data))
      }
      if (!data.isSeller) {
        setToken(data.access_token)
        return setActiveScreenSeller()
      }
      // Свежий вход продавца — показать модалку о сниженной комиссии один раз
      sessionStorage.setItem(FRESH_LOGIN_STORAGE_KEY, '1')
      dispatch(appActions.setToken(data))
    },
    [dispatch, setActiveScreenSeller, setToken]
  )

  const onSubmit = async (data: ISignInForm) => {
    try {
      setIsLoading(true)
      const response = await authApi.signIn({
        ...data,
        type: ELoginTypes.SELLER
      })
      applyLogin(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <Typography.Title className={styles.title}>Войти</Typography.Title>
      {method === 'phone' ? (
        <SignInPhone onSuccess={applyLogin} onSwitchToEmail={setEmailMethod} />
      ) : (
        <Form
          name='signIn'
          size='large'
          initialValues={{
            rememberMe: true
          }}
          onFinish={onSubmit}
        >
          <Form.Item
            name='email'
            normalize={(value) => value?.trim()}
            rules={[
              {
                type: 'email',
                message: 'Некорректный электронный адрес'
              },
              {
                required: true,
                message: 'Заполните электронный адрес'
              }
            ]}
            hasFeedback
          >
            <Input
              placeholder='Электронный адрес'
              prefix={<MailOutlined className='icon' />}
              autoComplete='email'
              autoCapitalize='none'
              autoCorrect='off'
            />
          </Form.Item>
          <Form.Item
            name='password'
            rules={[
              {
                required: true,
                message: 'Заполните пароль'
              }
            ]}
            hasFeedback
          >
            <Input.Password
              type='password'
              placeholder='Пароль'
              prefix={<LockOutlined className='icon' />}
            />
          </Form.Item>
          <div className={styles.signInActions}>
            <Form.Item
              name='rememberMe'
              valuePropName='checked'
              style={{ marginBottom: 0 }}
            >
              <Checkbox>Запомнить меня</Checkbox>
            </Form.Item>
            <Link to={routes.RECOVERY_PASSWORD}>
              <Button type='link' size='large' style={{ padding: 0 }}>
                Восстановить пароль
              </Button>
            </Link>
          </div>
          <Button
            block
            htmlType='submit'
            type='primary'
            icon={<LoginOutlined />}
            className={styles.signInButton}
            loading={isLoading}
          >
            Войти
          </Button>
          <Button
            block
            icon={<PhoneOutlined />}
            className={styles.switchMethodButton}
            onClick={setPhoneMethod}
          >
            Войти по телефону
          </Button>
        </Form>
      )}
      <Typography.Text className={styles.supportText}>
        Проблемы со входом?{' '}
        <a
          href={SUPPORT_URL}
          target='_blank'
          rel='noreferrer'
          className={styles.supportLink}
        >
          Напишите в техподдержку
        </a>
      </Typography.Text>
    </div>
  )
}
